import { $generateHtmlFromNodes, RichTextEditor } from '@talentmesh/rte';
import { Button } from '@talentmesh/ui/components/button';
import { TypographyP } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import { useCandidatesNoteFormContext } from './useCandidatesNoteForm';
import UIStrings from '@/Utils/UIStrings';

interface NoteRTEProps {
    saved: boolean;
    errorSaving: boolean;
}

function NoteRTE({ saved, errorSaving }: NoteRTEProps) {
    const {
        formState: { isDirty, isSubmitting, defaultValues },
        setValue,
        control,
    } = useCandidatesNoteFormContext();
    const { fieldState } = useController({ control, name: 'noteRte' });
    const [isInitialRender, setIsInitialRender] = useState(true);

    return (
        <div className="flex flex-col items-start gap-4" data-cy="NoteSection">
            <div
                className={cn(
                    'flex h-120 w-full flex-col rounded-xl border',
                    fieldState.error?.message ? 'border-destructive' : 'border-border',
                )}
            >
                <RichTextEditor
                    data-cy="NoteEditor"
                    initialHtml={defaultValues?.noteRte}
                    features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}
                    onChange={(editorState, editor) => {
                        editorState.read(() => {
                            setValue('noteRte', $generateHtmlFromNodes(editor), {
                                shouldValidate: !isInitialRender,
                                shouldDirty: true,
                            });
                            if (isInitialRender) {
                                setIsInitialRender(false);
                            }
                        });
                    }}
                    contentEditableProps={{
                        className: 'flex-1 overflow-y-auto',
                    }}
                />
                {fieldState.error?.message && (
                    <TypographyP className="text-destructive">{fieldState.error.message}</TypographyP>
                )}
            </div>
            <div className="flex flex-row items-center gap-5">
                <Button data-cy="NoteSubmitButton" size="lg" disabled={!isDirty || isSubmitting} type="submit">
                    {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
                    {UIStrings.Save}
                </Button>
                {isSubmitting && <TypographyP>{UIStrings.SavingWithDots}</TypographyP>}
                {saved && <TypographyP>{UIStrings.Saved}</TypographyP>}
                {errorSaving && <TypographyP className="text-destructive">{UIStrings.NoteCannotBeSaved}</TypographyP>}
            </div>
        </div>
    );
}

export default NoteRTE;
